import { useEffect, useMemo, useState } from 'react'
import { Property, Unity } from '@/custom-types';
import { PropertySimpleSelectionList } from './PropertySimpleSelectionList';
import { UnitySimpleSelectionList } from './UnitySimpleSelectionList';
import { OrderDirectionOption, PageSizeOption, UnityStatusOption, useGetPropertiesForContractsQuery } from '@/graphql/generated';
import {
  useCreateIncidentContext,
  useErrorHandlerContext,
  useUserLanguage,
} from '@/customHooks';
import { LoadingSpinner, RowSpacer, SquareButton2 } from '../atoms';
import { Col, Row } from 'react-grid-system';
import textsData from '@/textData/CreateIncidentTexts.json'
import houseIcon from '@/assets/property-icons/house-icon.svg'
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { ListFilterControls } from './ListFilterControls';
import { Pagination } from '../molecules';

export const SelectIncidentPropertyMenu = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.selectProperty : textsData.en.selectProperty;

  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null)
  const [selectedUnity, setSelectedUnity] = useState<Unity | null>(null)
  const [search, setSearch] = useState('')
  const [orderDirection, setOrderDirection] = useState<OrderDirectionOption>(OrderDirectionOption.Asc)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = PageSizeOption.TwentyFive

  const { setProperty, setUnity, setButtonDisabled, setButtonEnabled } = useCreateIncidentContext()

  const { open: errorHandle } = useErrorHandlerContext()

  const variables = useMemo(() => ({
    input: {
      search: search || undefined,
      orderDirection,
      page: currentPage.toString(),
      pageSize,
    }
  }), [orderDirection, search, currentPage, pageSize])

  useEffect(() => {
    setCurrentPage(1)
  }, [search, orderDirection])

  const { data, error, loading } = useGetPropertiesForContractsQuery({
    variables: variables,
  })

  const navigate = useNavigate()

  useEffect(() => {
    if (selectedProperty) {
      setProperty(selectedProperty);
      setButtonEnabled();
    } else {
      setButtonDisabled();
    }

    setSelectedUnity(null);
    setUnity(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty]);

  useEffect(() => {
    if (selectedUnity) {
      setUnity(selectedUnity.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnity]);

  const properties = useMemo(
    () => data?.properties?.items ?? [],
    [data],
  )

  const propertyMapper = (properties: Property[]) => properties.map((property) => ({
    id: property.id,
    name: property.name,
    street: property.address?.street ?? '',
    city: property.address.city?.name ?? '',
    state: property.address.state?.name ?? '',
    status: property.occupancyStatusOption
  }))

  const onPropertySelectHandle = (proeprtyId: string) => {
    const property = properties.find((property) => property.id === proeprtyId)
    if (property) {
      setSelectedProperty(property)
    }
  }

  if (error) errorHandle(error)

  return (
    <>
      <Row justify='center'>
        <Col xs='content'>
          <h2>{texts.property}</h2>
        </Col>
      </Row>
      <Row justify='center'>
        <Col xs='content'>
          <ListFilterControls
            search={search}
            onSearchChange={setSearch}
            orderDirection={orderDirection}
            onOrderChange={setOrderDirection}
          />
        </Col>
      </Row>
      {loading ? (
        <Row>
          <Col>
            <LoadingSpinner />
          </Col>
        </Row>
      ) : (
        <>
          <Row justify='center'>
            <Col xs='content'>
              <PropertySimpleSelectionList
                selected={(property) => onPropertySelectHandle(property.id)}
                properties={propertyMapper(properties)}
                disabledOn={(property) => property.status === UnityStatusOption.NotAvailable}
              />
            </Col>
          </Row>
          {data?.properties?.items && data.properties.items.length > 0 && (
            <Row justify='center'>
              <Col xs='content'>
                <Pagination
                  currentPage={currentPage}
                  onPageChange={setCurrentPage}
                  currentDataCount={data.properties.items.length}
                  pageSize={pageSize}
                  totalPages={data.properties.pagination?.totalPages}
                />
              </Col>
            </Row>
          )}
          <RowSpacer height={3} />
          <Row justify='center' >
            <Col xs='content' style={{ minHeight: '100px' }} hidden={!selectedProperty}>
              {selectedProperty && selectedProperty.unities && selectedProperty.unities.length > 0 ? (
                <>
                  <h2>{texts.unity}</h2>
                  <UnitySimpleSelectionList
                    selected={(unity) => setSelectedUnity(unity ?? null)}
                    unities={selectedProperty.unities}
                  />
                </>
              ) : (
                <p><b>{texts.noUnities}</b></p>
              )}
            </Col>
          </Row>
          <Row justify="center">
            <Col xs="content">
              <SquareButton2
                action={() => navigate(`/${pathURL.USER}/${pathURL.PROPERTY}/${pathURL.CREATE}`)}
              >
                <>
                  <img src={houseIcon} alt="" />
                  <span>{texts.addProperty}</span>
                </>
              </SquareButton2>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};
